import { NgIf } from "@angular/common";
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  Renderer2,
  signal,
  ViewChild
} from "@angular/core";
import { MatSliderModule } from "@angular/material/slider";
import { IconModule } from "src/app/shared/ui/icon/icon.module";
import { formatTime } from "./audio-player.utils";

enum Volume {
  OFF = 0,
  ON
}

@Component({
  selector: "tpm-audio-player",
  templateUrl: "audio-player.component.html",
  styleUrls: ["audio-player.component.scss"],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgIf, IconModule, MatSliderModule]
})
export class AudioPlayerComponent implements OnInit, OnDestroy {
  @ViewChild("player", { read: ElementRef, static: true })
  readonly player!: ElementRef;

  @Input({ required: true }) source: string;

  readonly currentTime = signal<number>(0);
  readonly durationTime = signal<number>(0);
  readonly progressTime = computed(() => `${formatTime(this.currentTime())} / ${formatTime(this.durationTime())}`);

  readonly isPlay = signal<boolean>(false);
  readonly isMuted = signal<boolean>(false);

  private readonly listeners: Map<string, () => void> = new Map([
    [
      "timeupdate",
      () => {
        this.currentTime.set(this.playerElement.currentTime);
      }
    ],
    [
      "loadedmetadata",
      () => {
        this.durationTime.set(this.playerElement.duration);
      }
    ],
    [
      "ended",
      () => {
        this.currentTime.set(0);
        this.togglePlay();
      }
    ]
  ]);

  get playerElement(): HTMLMediaElement {
    return this.player?.nativeElement;
  }

  constructor(private readonly renderer: Renderer2) {}

  ngOnInit(): void {
    this.listeners.forEach((callback, eventName) => this.playerElement.addEventListener(eventName, callback));
  }

  ngOnDestroy(): void {
    this.playerElement.pause();
    this.listeners.forEach((callback, eventName) => this.playerElement.removeEventListener(eventName, callback));
    this.listeners.clear();
  }

  togglePlay(): void {
    if (this.isPlay()) {
      this.playerElement.pause();
    } else {
      this.playerElement.play();
    }

    this.isPlay.set(!this.isPlay());
  }

  toggleVolume(): void {
    this.isMuted.set(!this.isMuted());
    this.renderer.setProperty(this.playerElement, "volume", this.isMuted() ? Volume.OFF : Volume.ON);
  }

  onSliderValueChange(value: number): void {
    this.playerElement.currentTime = value;
  }
}
